/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
/* eslint-disable multiline-ternary */
/* eslint-disable complexity */
/* eslint-disable max-len */
import * as TOML from "@ltd/j-toml";
import { guessQuestionTypeByName } from "./question-type-specs";
import { applyQuestionDataPresets } from "./form-builder-question-data-presets";
import {
  isValidQuestionIdentifier,
  QUESTION_IDENTIFIER_RULE,
  isValidContextValue,
  CONTEXT_VALUE_RULE,
  isValidSectionIdentifier,
  SECTION_IDENTIFIER_RULE,
} from "./question-identifier-validation";

export const IDENTITY_SEPARATOR = " :: ";

export type Identity = string[];

export type BuilderQuestion = {
  draftId: string;
  name: string;
  contextText: string;
  title: string;
  required: boolean;
  alias: string;
  condValuesText: string;
  childrenKeys: string[];
  dataJson: string;
};

export type BuilderSection = {
  draftId: string;
  name: string;
  title: string;
  description: string;
  questionKeys: string[];
};

export type BuilderDraft = {
  layoutName: string;
  sections: BuilderSection[];
  questions: BuilderQuestion[];
  extras: Record<string, unknown>;
};

type BuildResult = {
  content: string;
  errors: string[];
  warnings: string[];
};

const nextDraftId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const identityToKey = (identity: Identity): string => identity.join(IDENTITY_SEPARATOR);

export const keyToIdentity = (key: string): Identity =>
  key
    .split(IDENTITY_SEPARATOR)
    .map((value) => value.trim())
    .filter(Boolean);

// A question is uniquely identified by [name, context]. Multi-element contexts
// are collapsed to a single underscore-joined token so the data model stays
// single-context. Mirrors the server (Forms.Data.Form) and the
// Haven.CleanFormDraftContexts mix task.
export const collapseIdentity = (identity: Identity): Identity =>
  (identity.length > 2 ? [identity[0], identity.slice(1).join("_")] : identity);

const contextToText = (identity: Identity): string => identity.slice(1).join(", ");

const textToContext = (contextText: string): string[] =>
  contextText
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

const asIdentity = (value: unknown): Identity | null => {
  if (!Array.isArray(value)) return null;

  const identity = value.map((item) => `${item}`.trim()).filter(Boolean);
  if (identity.length === 0) return null;

  return identity;
};

const asIdentityList = (value: unknown): Identity[] => {
  if (!Array.isArray(value)) return [];
  return value.map(asIdentity).filter((identity): identity is Identity => identity !== null);
};

const toPrettyJSON = (value: unknown): string => JSON.stringify(value, null, 2);

const ensureObject = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
};

const cloneTomlValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(cloneTomlValue);
  }

  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .reduce<Record<string, unknown>>((acc, [key, nestedValue]) => {
      acc[key] = cloneTomlValue(nestedValue);
      return acc;
    }, {});
  }

  return value;
};

const cloneTomlObject = (value: Record<string, unknown>): Record<string, unknown> =>
  cloneTomlValue(value) as Record<string, unknown>;

const parseCondValues = (question: Record<string, any>): string => {
  const values = question.cond?.type === "includes" && Array.isArray(question.cond?.values)
    ? question.cond.values
    : [];

  return values.join(", ");
};

const draftQuestionFromIdentity = (identity: Identity): BuilderQuestion => ({
  draftId: nextDraftId(),
  name: identity[0] || "",
  contextText: contextToText(identity),
  title: "",
  required: false,
  alias: "",
  condValuesText: "",
  childrenKeys: [],
  dataJson: "{}",
});

const questionIdentityFromDraft = (question: BuilderQuestion): Identity =>
  [question.name.trim(), ...textToContext(question.contextText)].filter(Boolean);

export const emptyDraftQuestion = (): BuilderQuestion => ({
  draftId: nextDraftId(),
  name: "",
  contextText: "applicant",
  title: "",
  required: false,
  alias: "",
  condValuesText: "",
  childrenKeys: [],
  dataJson: "{}",
});

export const emptyDraftSection = (): BuilderSection => ({
  draftId: nextDraftId(),
  name: "",
  title: "",
  description: "",
  questionKeys: [],
});

export const parseBuilderDraft = (content: string): { draft: BuilderDraft; errors: string[] } => {
  const fallbackDraft: BuilderDraft = {
    layoutName: "apply",
    sections: [emptyDraftSection()],
    questions: [],
    extras: {},
  };

  if (!content.trim()) {
    return { draft: fallbackDraft, errors: [] };
  }

  try {
    const parsed = TOML.parse[0.5](content, "\n") as Record<string, any>;
    const { layout, question, ...extras } = parsed;

    const layoutBlock = Array.isArray(layout) && layout.length > 0 ? layout[0] : {};
    const sectionsRaw = Array.isArray(layoutBlock?.group) ? layoutBlock.group : [];
    const questionRaw = Array.isArray(question) ? question : [];

    const questions = questionRaw.map((questionItem) => {
      const identity = asIdentity(questionItem.name) || [""];
      const type = guessQuestionTypeByName(identity[0] || "");
      const data = ensureObject(questionItem.data);

      const childrenFromQuestion = asIdentityList(questionItem.children);
      const childrenFromData = asIdentityList(data.children);
      const children = (childrenFromQuestion.length > 0 ? childrenFromQuestion : childrenFromData)
        .map(identityToKey);

      const dataWithoutArrayChildren =
        type === "array"
          ? Object.keys(data).reduce<Record<string, unknown>>((acc, key) => {
            if (key !== "children") acc[key] = data[key];
            return acc;
          }, {})
          : data;

      return {
        draftId: nextDraftId(),
        name: identity[0] || "",
        contextText: contextToText(identity),
        title: questionItem.title || "",
        required: Boolean(questionItem.required),
        alias: questionItem.alias || "",
        condValuesText: parseCondValues(questionItem),
        childrenKeys: children,
        dataJson: toPrettyJSON(dataWithoutArrayChildren),
      };
    });

    const questionsByIdentity = new Set(
      questions.map((questionItem) => identityToKey(questionIdentityFromDraft(questionItem))),
    );

    const sections = sectionsRaw.map((sectionItem) => {
      const questionKeys = asIdentityList(sectionItem.questions).map(identityToKey);

      return {
        draftId: nextDraftId(),
        name: sectionItem.name || "",
        title: sectionItem.title || "",
        description: sectionItem.description || "",
        questionKeys,
      };
    });

    sections.forEach((section) => {
      section.questionKeys.forEach((questionKey) => {
        if (!questionsByIdentity.has(questionKey)) {
          questions.push(draftQuestionFromIdentity(keyToIdentity(questionKey)));
          questionsByIdentity.add(questionKey);
        }
      });
    });

    const sanitizedSections = sections.length > 0 ? sections : [emptyDraftSection()];

    return {
      draft: {
        layoutName: layoutBlock?.name || "apply",
        sections: sanitizedSections,
        questions,
        extras: cloneTomlObject(extras || {}),
      },
      errors: [],
    };
  } catch (error) {
    return {
      draft: fallbackDraft,
      errors: [`Unable to parse TOML: ${String(error)}`],
    };
  }
};

export const buildContentFromDraft = (
  draft: BuilderDraft,
  existingQuestions: Record<string, string>,
): BuildResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const questionObjects: Record<string, any>[] = [];
  const knownQuestionKeys = new Set<string>();
  const questionKeyToIdentity = new Map<string, Identity>();
  const allQuestionKeys = new Set(
    draft.questions.map((question) => identityToKey(questionIdentityFromDraft(question))),
  );

  if (draft.questions.length === 0) {
    return { content: "", errors: ["At least one question is required."], warnings: [] };
  }

  if (draft.sections.length === 0) {
    return { content: "", errors: ["At least one section is required."], warnings: [] };
  }

  draft.questions.forEach((question) => {
    const identity = questionIdentityFromDraft(question);
    const questionLabel = question.title || identityToKey(identity) || "(new question)";

    if (!identity[0]) {
      errors.push(`Question "${questionLabel}" is missing a name.`);
      return;
    }

    if (!isValidQuestionIdentifier(identity[0])) {
      errors.push(
        `Question "${questionLabel}" has invalid name "${identity[0]}". Use ${QUESTION_IDENTIFIER_RULE}.`,
      );
    }

    const invalidContextValues = identity.slice(1).filter(
      (contextValue) => !isValidContextValue(contextValue),
    );
    if (invalidContextValues.length > 0) {
      errors.push(
        `Question "${questionLabel}" has invalid context values: ${
          invalidContextValues.join(", ")
        }. Use ${CONTEXT_VALUE_RULE}.`,
      );
    }

    const alias = question.alias.trim();
    if (alias && !isValidQuestionIdentifier(alias)) {
      errors.push(
        `Question "${questionLabel}" has invalid alias "${alias}". Use ${QUESTION_IDENTIFIER_RULE}.`,
      );
    }

    const questionKey = identityToKey(identity);
    const collapsed = collapseIdentity(identity);
    const collapsedKey = identityToKey(collapsed);
    if (knownQuestionKeys.has(collapsedKey)) {
      errors.push(`Question identity ${collapsedKey} is duplicated.`);
      return;
    }

    knownQuestionKeys.add(collapsedKey);
    // Sections/children still reference the author-entered (pre-collapse) key.
    questionKeyToIdentity.set(questionKey, collapsed);

    if (collapsedKey !== questionKey) {
      warnings.push(`Merged context: ${collapsedKey}`);
    }

    let parsedData: Record<string, unknown> = {};
    if (question.dataJson.trim()) {
      try {
        const candidate = JSON.parse(question.dataJson);
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
          errors.push(`Question ${questionKey} has invalid data JSON. Expected object.`);
          return;
        }
        parsedData = candidate as Record<string, unknown>;
      } catch (error) {
        errors.push(`Question ${questionKey} has invalid data JSON: ${String(error)}`);
        return;
      }
    }

    const condValues = question.condValuesText
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    const questionType = existingQuestions[identity[0]] || guessQuestionTypeByName(identity[0]) || "";
    parsedData = applyQuestionDataPresets(identity[0], questionType, parsedData);
    const childKeys = question.childrenKeys
      .filter((questionKeyValue) => questionKeyValue !== questionKey)
      .filter((questionKeyValue) => {
        if (!allQuestionKeys.has(questionKeyValue)) {
          errors.push(`Question ${questionKey} links unknown child ${questionKeyValue}.`);
          return false;
        }

        return true;
      });
    const childIdentities = childKeys.map(
      (questionKeyValue) => collapseIdentity(keyToIdentity(questionKeyValue)),
    );

    const questionObject: Record<string, any> = {
      name: collapsed,
      title: question.title,
      required: question.required,
    };

    if (alias) {
      questionObject.alias = alias;
    }

    if (condValues.length > 0) {
      questionObject.cond = { type: "includes", values: condValues };
    }

    if (questionType === "array") {
      if (childIdentities.length > 0) {
        parsedData.children = childIdentities;
      }
    } else if (childIdentities.length > 0) {
      questionObject.children = childIdentities;
    }

    if (Object.keys(parsedData).length > 0) {
      questionObject.data = parsedData;
    }

    questionObjects.push(questionObject);
  });

  draft.sections.forEach((section, index) => {
    const sectionName = section.name.trim();
    const sectionTitle = section.title.trim();
    const sectionDescription = section.description.trim();

    if (!sectionName) {
      errors.push(`Section #${index + 1} is missing a name.`);
    } else if (!isValidSectionIdentifier(sectionName)) {
      errors.push(
        `Section "${sectionName}" has invalid name. Use ${SECTION_IDENTIFIER_RULE}.`,
      );
    }

    if (!sectionTitle) {
      errors.push(`Section "${section.name || index + 1}" is missing a title.`);
    }

    if (!sectionDescription) {
      errors.push(`Section "${section.name || index + 1}" is missing a description.`);
    }
  });

  if (errors.length > 0) {
    return { content: "", errors, warnings: [] };
  }

  const groups = draft.sections.map((section) => ({
    name: section.name.trim(),
    title: section.title.trim(),
    description: section.description.trim(),
    questions: section.questionKeys
      .map((key) => questionKeyToIdentity.get(key))
      .filter((identity): identity is Identity => Array.isArray(identity)),
  }));

  const payload = {
    ...draft.extras,
    layout: [
      {
        name: draft.layoutName || "apply",
        group: groups,
      },
    ],
    question: questionObjects,
  };

  const encoded = TOML.stringify(payload);
  const content = Array.isArray(encoded) ? encoded.join("\n") : encoded;
  return { content, errors: [], warnings };
};

export const readableIdentity = (questionKey: string): string => {
  const identity = keyToIdentity(questionKey);
  if (identity.length === 0) return "(invalid identity)";
  const [name, ...context] = identity;
  if (context.length === 0) return name;
  return `${name} [${context.join(" / ")}]`;
};
